<template>
  <form @submit.prevent="createBill()" action="">
    <ion-row>
      <ion-col size="12">
        <ion-list>
          <ion-item v-if="validationError" lines="none">
            <ion-label className="ion-text-wrap">
              <ion-text color="danger">
                <p v-for="error of v$.$errors" :key="error.$uid">
                  <ion-icon
                    :ios="alertCircleOutline"
                    :md="alertCircleSharp"
                    size="small"
                    style="vertical-align: middle"
                  ></ion-icon
                  >{{ error.$property }}: {{ error.$message }}
                </p></ion-text
              >
            </ion-label>
          </ion-item>
          <ion-item :class="{ 'color-danger': v$.name.$error }">
            <ion-label position="floating">Bill Name</ion-label>
            <ion-input type="text" v-model="name"></ion-input>
          </ion-item>
          <ion-item :class="{ 'color-danger': v$.description.$error }">
            <ion-label position="floating">Description</ion-label>
            <ion-input type="text" v-model="description"></ion-input>
          </ion-item>
          <ion-item :class="{ 'color-danger': v$.bill_from_date.$error }">
            <ion-label position="floating">Bill from date</ion-label>
            <ion-datetime type="text" v-model="bill_from_date" max="2030-12-31"></ion-datetime>
          </ion-item>
          <ion-item :class="{ 'color-danger': v$.bill_to_date.$error }">
            <ion-label position="floating">Bill to date</ion-label>
            <ion-datetime type="text" v-model="bill_to_date" max="2030-12-31"></ion-datetime>
          </ion-item>
          <ion-item :class="{ 'color-danger': v$.bill_due_date.$error }">
            <ion-label position="floating">Bill due date</ion-label>
            <ion-datetime type="text" v-model="bill_due_date" max="2030-12-31"></ion-datetime>
          </ion-item>

          <ion-item :class="{ 'color-danger': v$.session_id.$error }">
            <ion-label position="floating">Academic session</ion-label>
            <ion-select v-model="session_id">
              <ion-select-option
                v-for="session of sessions"
                :key="session.id"
                :value="session.id"
              >
                {{ session.name }}
              </ion-select-option>
            </ion-select>
          </ion-item>

          <ion-item :class="{ 'color-danger': v$.fee_ids.$error }">
            <ion-label position="floating">Fees</ion-label>
            <ion-select v-model="fee_ids" multiple>
              <ion-select-option
                v-for="fee of fees"
                :key="fee.id"
                :value="fee.id"
              >
                {{ fee.name }}
              </ion-select-option>
            </ion-select>
          </ion-item>
        </ion-list>
      </ion-col>
    </ion-row>
    <ion-row>
      <ion-col>
        <ion-button type="submit" expand="full" class="ion-margin-vertical">
          Save
        </ion-button>
      </ion-col>
    </ion-row>
  </form>
</template>

<script>
import {
  IonRow,
  IonCol,
  IonList,
  IonLabel,
  IonItem,
  IonIcon,
  IonButton,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonDatetime,
  IonText,
  loadingController,
  alertController,
} from "@ionic/vue";

import { alertCircleOutline, alertCircleSharp } from "ionicons/icons";

import { required, maxLength, integer, helpers } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";

export default {
  emits: ["closeModal"],
  components: {
    IonRow,
    IonCol,
    IonList,
    IonLabel,
    IonItem,
    IonIcon,
    IonButton,
    IonInput,
    IonSelect,
    IonSelectOption,
    IonDatetime,
    IonText,
  },
  data() {
    return {
      name: null,
      description: null,
      bill_from_date: null,
      bill_to_date: null,
      bill_due_date: null,
      session_id: null,
      fee_ids: null,

      validationError: null,

      alertCircleOutline,
      alertCircleSharp,

      xhrError: {
        header: null,
        description: null,
      },
    };
  },

  setup() {
    return { v$: useVuelidate() };
  },

  validations() {
    return {
      name: {
        required: helpers.withMessage("cannot be empty", required),
        maxLength: helpers.withMessage(
          "maximum 255 characters",
          maxLength(255)
        ),
      },
      description: {
        required: helpers.withMessage("cannot be empty", required),
        maxLength: helpers.withMessage(
          "maximum 255 characters",
          maxLength(255)
        ),
      },
      bill_from_date: {
        maxLength: helpers.withMessage("maximum 255 ccharacters", maxLength(255)),
      },
      bill_to_date: {
        maxLength: helpers.withMessage("maximum 255 ccharacters", maxLength(255)),
      },
      bill_due_date: {
        maxLength: helpers.withMessage("maximum 255 ccharacters", maxLength(255)),
      },
      session_id: {
        integer: helpers.withMessage("can only be integers", integer),
      },
      fee_ids: {
        required: helpers.withMessage("is required", required),
      },
    };
  },

  computed: {
    sessions() {
      return this.$store.getters["session/allResources"];
    },
    fees() {
      return this.$store.getters["fee/allResources"];
    },
  },

  methods: {
    setModalState(state) {
      this.modalState = state;
    },

    loader(message) {
      const loading = loadingController.create({
        message: message,
        backdropDismiss: false,
      });

      return loading;
    },

    alerter(header, message, buttons = ["close"]) {
      const alert = alertController.create({
        header: header,
        message: message,
        buttons: buttons,
      });

      return alert;
    },

    setErrorResponse(error) {
      if (error.response) {
        this.xhrError.header = error.response.data.header;
        this.xhrError.message = error.response.data.message;
      } else if (error.request) {
        this.xhrError.header = error.message || error.request;
        this.xhrError.message = "Please check your connection and try again";
      } else {
        this.xhrError.header = error.message;
        this.xhrError.message = "Something went wrong. Try again later";
      }
    },

    async createBill() {
      this.v$.$touch();
      if (this.v$.$error) {
        this.validationError = true;
        return;
      }

      let data = {
        name: this.name,
        description: this.description,
        bill_from_date: this.bill_from_date,
        bill_to_date: this.bill_to_date,
        bill_due_date: this.bill_due_date,
        session_id: this.session_id,
        fee_ids: this.fee_ids,
      };

      const spinner = await this.loader("Saving...");
      spinner.present();

      try {
        await this.$store.dispatch("bill/createResource", data);
        spinner.dismiss();

        this.name = null;
        this.$emit("closeModal");
      } catch (error) {
        spinner.dismiss();

        this.setErrorResponse(error);

        const alertDialog = await this.alerter(
          this.xhrError.header,
          this.xhrError.message
        );

        alertDialog.present();

        this.password = null;
        this.validationError = null;
      }
    },

    async fetchSessions() {
      await this.$store.dispatch("session/allResources");
    },
    async fetchFees() {
      await this.$store.dispatch("fee/allResources");
    },
  },

  created() {
    this.fetchSessions();
    this.fetchFees();
  },
};
</script>